import React from 'react';
import {useIntl} from 'react-intl';
import styled from 'styled-components';
import GenericModal from '../generic_modal';
import Util from '../../../forecast-app/shared/util/util';
import {BUTTON_COLOR, BUTTON_STYLE} from '../../../constants';
import {MODAL_TYPE, showModal} from '../../../forecast-app/shared/components/modals/generic_modal_conductor';
import {ModalProps} from '../../../forecast-app/shared/components/modals/types';
import {formatSetDate} from '../../../forecast-app/project-tab/projects/scoping-page/task-set/describeTaskSet';

/** One existing instance, as the ends preflight reports it. */
export interface EndsCandidate {
	readonly sequence?: number | null;
	readonly startDate?: string | null;
	/** Minutes on an hours project, points on a points one. */
	readonly estimateForecast?: number | null;
	readonly task?: {readonly estimateForecastPrice?: number | null} | null;
}

export interface TaskSetEndsModalPayload {
	/** Where the set ends today. Null on a set that has never been bounded by date. */
	currentEndDate?: string | null;
	/** The date the reader picked: the last date an instance may START. */
	newEndDate: string;
	/** Every instance the set has today, in sequence order. */
	instances: ReadonlyArray<EndsCandidate>;
	isPointsProject?: boolean;
	/** Absent where the project tracks no revenue, which hides the revenue row. */
	currencySymbol?: string | null;
	onConfirm: () => void;
}

export const showTaskSetEndsModal = (props: TaskSetEndsModalPayload): void => {
	showModal({
		type: MODAL_TYPE.TASK_SET_ENDS,
		...props,
	});
};

const Lead = styled.p`
	margin: 0 0 14px;
	font-size: 13px;
	line-height: 19px;
	color: rgba(0, 0, 0, 0.87);
`;

const Table = styled.div`
	display: grid;
	grid-template-columns: 1fr auto auto;
	gap: 8px 18px;
	align-items: baseline;
	padding: 12px;
	border: 1px solid #e6e6e6;
	border-radius: 4px;
`;

const HeadCell = styled.span`
	justify-self: end;
	font-size: 11px;
	font-weight: 600;
	letter-spacing: 0.4px;
	text-transform: uppercase;
	color: #7d7d7d;
`;

const RowLabel = styled.span`
	font-size: 13px;
	color: rgba(0, 0, 0, 0.87);
`;

const Figure = styled.span<{emphasis?: boolean}>`
	justify-self: end;
	font-size: 13px;
	font-variant-numeric: tabular-nums;
	font-weight: ${({emphasis}) => (emphasis ? 600 : 400)};
	color: ${({emphasis}) => (emphasis ? 'rgba(0, 0, 0, 0.87)' : '#7d7d7d')};
`;

const Note = styled.p`
	margin: 12px 0 0;
	font-size: 12px;
	line-height: 18px;
	color: #7d7d7d;
`;

/**
 * Confirming that a set will end EARLY.
 *
 * <p>Only a shorten reaches here. Pushing the end out destroys nothing and moves
 * no existing instance's date, so it passes without a dialog and the number it
 * adds is stated on the field itself. A confirmation for a harmless change is
 * what teaches people to click through the one that is not.
 *
 * <p>Its own confirmation rather than a line in the scope chooser, because it is
 * a different kind of change: every other field on the edit form rewrites
 * attributes on work that stays, and this destroys the work itself.
 *
 * <p><b>The date is the last date an instance may START</b>, so the instance
 * falling on it survives. Every count here follows from that one sentence -- get
 * it wrong and the modal is off by one in the direction that deletes something
 * the reader was told would stay.
 */
export const TaskSetEndsModal = ({
	closeModal,
	currentEndDate,
	newEndDate,
	instances,
	isPointsProject,
	currencySymbol,
	onConfirm,
}: ModalProps<TaskSetEndsModalPayload>): JSX.Element => {
	const intl = useIntl();
	const {formatMessage} = intl;

	// Strictly after: the instance starting ON the new date is the one the field
	// names, and it stays. The backend applies the same comparison; these two
	// agreeing is what makes the count on the button true. Compared as ISO
	// strings, which sort as dates without a parse.
	const destroyed = instances.filter(instance => !!instance.startDate && instance.startDate > newEndDate);
	const kept = instances.filter(instance => !instance.startDate || instance.startDate <= newEndDate);

	const sumEstimate = (rows: ReadonlyArray<EndsCandidate>): number =>
		rows.reduce((total, row) => total + (row.estimateForecast || 0), 0);
	const sumPrice = (rows: ReadonlyArray<EndsCandidate>): number =>
		rows.reduce((total, row) => total + (row.task?.estimateForecastPrice || 0), 0);

	/* Minutes on an hours project and points on a points one -- the same stored
	   number means different things, so it is converted in one place and labelled
	   to match rather than being called hours everywhere. */
	const asUnits = (stored: number): number => (isPointsProject ? stored : stored / 60);
	const formatUnits = (stored: number): string => intl.formatNumber(Math.round(asUnits(stored) * 100) / 100);

	const unitsLabel = formatMessage({id: isPointsProject ? 'task_set.ends.row_points' : 'task_set.ends.row_hours'});

	const content = (
		<div data-cy="task-set-ends-modal-content">
			<Lead>
				{formatMessage(
					{id: 'task_set.ends.shorten_lead'},
					{
						from: formatSetDate(intl, currentEndDate) ?? formatMessage({id: 'task_set.none'}),
						to: formatSetDate(intl, newEndDate),
						count: destroyed.length,
					}
				)}
			</Lead>

			<Table>
				<HeadCell />
				<HeadCell>{formatMessage({id: 'task_set.ends.column_now'})}</HeadCell>
				<HeadCell>{formatMessage({id: 'task_set.ends.column_after'})}</HeadCell>

				<RowLabel>{formatMessage({id: 'task_set.ends.row_instances'})}</RowLabel>
				<Figure data-cy="task-set-ends-instances-now">{instances.length}</Figure>
				<Figure emphasis data-cy="task-set-ends-instances-after">
					{kept.length}
				</Figure>

				<RowLabel>{unitsLabel}</RowLabel>
				<Figure data-cy="task-set-ends-estimate-now">{formatUnits(sumEstimate(instances))}</Figure>
				<Figure emphasis data-cy="task-set-ends-estimate-after">
					{formatUnits(sumEstimate(kept))}
				</Figure>

				{currencySymbol ? (
					<>
						<RowLabel>{formatMessage({id: 'task_set.ends.row_revenue'})}</RowLabel>
						<Figure data-cy="task-set-ends-revenue-now">
							{Util.getFormattedNumberWithCurrency(currencySymbol, sumPrice(instances), intl)}
						</Figure>
						<Figure emphasis data-cy="task-set-ends-revenue-after">
							{Util.getFormattedNumberWithCurrency(currencySymbol, sumPrice(kept), intl)}
						</Figure>
					</>
				) : null}
			</Table>

			{/* Said once, plainly. A reader who has not deleted a task before has no
			    reason to know that its time entries, comments and files go with it. */}
			<Note>{formatMessage({id: 'task_set.ends.destroyed_note'})}</Note>
		</div>
	);

	const handleConfirm = () => {
		onConfirm();
		closeModal();
	};

	return (
		<GenericModal
			closeModal={closeModal}
			cy="task-set-ends-modal"
			headerText={formatMessage({id: 'task_set.ends.title'})}
			content={content}
			buttons={[
				{
					text: formatMessage({id: 'common.cancel'}),
					style: BUTTON_STYLE.FILLED,
					color: BUTTON_COLOR.WHITE,
					cy: 'task-set-ends-cancel',
				},
				{
					// The count rides on the button, and it names what happens: a reader
					// about to destroy work should read the word delete before they press
					// it, not after.
					text: formatMessage({id: 'task_set.ends.confirm_shorten'}, {count: destroyed.length}),
					callback: handleConfirm,
					style: BUTTON_STYLE.FILLED,
					color: BUTTON_COLOR.RED,
					cy: 'task-set-ends-confirm',
				},
			]}
		/>
	);
};

export default TaskSetEndsModal;
